import { Head, useForm } from "@inertiajs/react"
import { useEffect, useRef, useState, type FormEvent } from "react"
import { track } from "@/lib/analytics"
import { PropertyVideoPhone } from "@/components/public/property-video-phone"
import { DistributionDiagram } from "@/components/public/distribution-diagram"
import type { RealExample } from "@/data/real-examples"
import type { SeoMeta } from "@/types"

// Real Canadian QuiteRight production video — 45 Ivy Lane, Orillia, Ontario.
// Same source listing the Meta ad creative uses (`marketing/public/videos/listings/canada.mp4`),
// served from CloudFront for the in-browser PropertyVideoPhone embed.
const CANADA_VIDEO_URL =
  "https://dudpw00jtfdjq.cloudfront.net/videos/2/eia3jz9qwaez/CLs8T3EzXI3q.mp4"

// Real Canadian listing used by the distribution diagram. Photos are the same
// four assets the Meta ad creative uses (Exterior, Boathouse, Aerial, Dock),
// copied to Rails public/ so the LP can serve them as static web assets.
const CANADA_EXAMPLE: RealExample = {
  property: "45 Ivy Lane, Orillia",
  location: "Ontario",
  price: "$732,000",
  beds: 5,
  baths: 4,
  propertyImage: "/images/listings/canada/photo-1.webp",
  thumbnails: [
    "/images/listings/canada/photo-2.webp",
    "/images/listings/canada/photo-3.webp",
    "/images/listings/canada/photo-4.webp",
  ],
  videoUrl: CANADA_VIDEO_URL,
  agency: "QuiteRight",
  sourceUrl: "https://www.quiteright.co.uk/v/eia3jz9qwaez",
  shareUrl: "https://www.quiteright.co.uk/v/eia3jz9qwaez",
}

const CAMPAIGN_SLUG = "canada-first-50"

interface Props {
  meta: SeoMeta
  submitted?: boolean
}

const PAIN_POINTS = [
  "You're looking at the same Realtor.ca carousel every other agent in your brokerage is posting. Identical photos. Identical layout. Identical scroll-past.",
  "You've thought about hiring a videographer. Four-hundred dollars per listing, a week's turnaround, and the result feels like a corporate explainer that nobody watches.",
  "Sunday night, you tell yourself: more video this week. By Wednesday you're back to the listing carousel because there's no time to learn another tool.",
]

const BENEFITS = [
  {
    title: "Look like a national brand.",
    body: "Cinematic videos make a single-agent practice look like a brokerage marketing team — without the brokerage marketing budget.",
  },
  {
    title: "Win the next listing presentation.",
    body: "Walk in with marketing that out-classes the RE/MAX team agent's pitch and watch the seller lean forward.",
  },
  {
    title: "Charge what you're worth.",
    body: "Premium presentation justifies premium commission. Sellers pay more when they can see the difference.",
  },
  {
    title: "Minutes, not days.",
    body: "Paste, pick, post. A few minutes start to finish. No videographer brief, no editor revisions, no waiting on a film crew.",
  },
  {
    title: "Every listing, not just the trophies.",
    body: "Cheap enough for the $400K starter, polished enough for the $4M waterfront. Same tool, same minute.",
  },
  {
    title: "Built for Canadian listings.",
    body: "Realtor.ca, your brokerage page, your own website — paste the URL, we pull the photos, the price, the address. Voiceover in Canadian English.",
  },
]
const STEPS = [
  {
    n: "I",
    title: "Paste",
    body: "Drop your listing URL — Realtor.ca, your brokerage page, your own site. We pull the photos, the price, the address, the description.",
  },
  {
    n: "II",
    title: "Pick",
    body: "Choose a template (Classic, Urban, Family, or Prestige) to match the property and the buyer.",
  },
  {
    n: "III",
    title: "Post",
    body: "Download the video square, vertical, or landscape, and put it wherever your next seller is scrolling.",
  },
]

const STATS = [
  { value: "15s", label: "Video length" },
  { value: "4", label: "Templates" },
  { value: "Any", label: "Listing portal" },
  { value: "50", label: "Spots in Canada" },
]

const FAQ = [
  {
    q: "Why a waitlist? Why not just sign me up?",
    a: "QuiteRight is built and shipping to UK agents today. We're picking the first fifty Canadian realtors to onboard manually so we can make sure the listing scraper handles Realtor.ca, your local MLS, and your brokerage's own site cleanly before opening the doors. Reserve your spot, we'll be in touch within a few days.",
  },
  {
    q: "What does it cost?",
    a: "Pricing for Canada hasn't been finalised. The first fifty agents get founder pricing — locked in for as long as you stay subscribed. We'll send you the number when we onboard you, and you'll have plenty of time to decide before you owe us a cent.",
  },
  {
    q: "Will it work with Realtor.ca?",
    a: "That's exactly what we're using the first fifty to confirm. The product handles Rightmove, Zoopla, OnTheMarket, and any agency website in the UK. The Canadian launch tests Realtor.ca and the major Canadian brokerage CMS templates. If your listing site doesn't work cleanly out of the gate, we'll fix it — and you'll be the reason we did.",
  },
  {
    q: "Is the voiceover Canadian?",
    a: "Yes. We've added Canadian English voices specifically for this launch. The video on this page uses one of them — a real Canadian listing in Orillia, Ontario, narrated in Canadian English. Your videos will sound the same.",
  },
  {
    q: "What's the catch?",
    a: "There isn't one. The waitlist is free. Reserving your spot doesn't commit you to anything. If we onboard you and the product doesn't fit your workflow, you walk away. We're betting that once you see your own listing rendered as a 15-second video, you'll want a second one. That's where we make our money.",
  },
]

const CTA_LABEL = "Reserve my spot"
const CTA_MICRO = "Free. No card. About sixty seconds to fill in."
const CTA_TARGET = "#waitlist-form"
function CallToAction({ tone = "light" }: { tone?: "light" | "dark" }) {
  const onClick = () => {
    track("cta_click", { campaign: CAMPAIGN_SLUG })
  }
  const microTone = tone === "dark" ? "text-stone/80" : "text-warm-grey"
  return (
    <div className="flex flex-col items-center">
      <a
        href={CTA_TARGET}
        onClick={onClick}
        className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-none bg-red px-10 py-5 text-base font-semibold uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_-18px_rgba(196,30,42,0.65)] transition-all duration-300 hover:bg-deep-red sm:w-auto sm:text-[15px]"
      >
        <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/15 to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-90" />
        <span className="relative">{CTA_LABEL}</span>
        <span aria-hidden className="relative ml-3 transition-transform duration-300 group-hover:translate-x-1">
          ↓
        </span>
      </a>
      <p className={`mt-4 text-center text-[11px] font-medium uppercase tracking-[0.18em] ${microTone}`}>
        {CTA_MICRO}
      </p>
    </div>
  )
}

function HeroStage() {
  return (
    <section className="relative overflow-hidden bg-charcoal text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 50% 45%, rgba(196,30,42,0.18) 0%, rgba(196,30,42,0.05) 30%, rgba(26,26,26,0) 65%), radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.5) 0%, transparent 60%)",
        }}
      />
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-red" />

      <div className="relative mx-auto max-w-2xl px-6 pt-14 pb-20 text-center md:pt-20 md:pb-28">
        <div className="font-display text-[22px] font-bold tracking-tight">
          <span className="text-cream">Quite</span>
          <span className="text-red">Right.</span>
        </div>

        <p className="mt-12 text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
          🇨🇦 &nbsp; Now picking the first 50 Canadian realtors
        </p>

        <h1 className="mt-8 font-display text-[40px] font-bold leading-[1.05] tracking-tight text-cream sm:text-[56px] md:text-[68px]">
          Be one of the first 50<span className="text-red">.</span>
        </h1>

        <p className="mx-auto mt-7 max-w-xl text-[15px] leading-relaxed text-stone/80 sm:text-base">
          QuiteRight is launching in Canada. Paste a listing URL, get a cinematic
          15-second video. We&rsquo;re onboarding the first fifty realtors by
          hand &mdash; reserve your spot below.
        </p>

        <div className="mt-10 flex justify-center">
          <CallToAction tone="dark" />
        </div>

        <div className="mt-16 mb-6">
          <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">
            ⌄ &nbsp; A real listing, on QuiteRight
          </p>
        </div>

        <div className="relative mx-auto">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-12 -inset-y-8"
            style={{
              background:
                "radial-gradient(ellipse 60% 50% at 50% 55%, rgba(196,30,42,0.35) 0%, rgba(196,30,42,0.08) 35%, transparent 70%)",
              filter: "blur(20px)",
            }}
          />
          <div className="relative">
            <PropertyVideoPhone
              videoUrl={CANADA_VIDEO_URL}
              label="Real QuiteRight video for 45 Ivy Lane, Orillia, Ontario, narrated in Canadian English"
              width={260}
            />
          </div>
          <div className="mx-auto mt-8 max-w-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">
              45 Ivy Lane &middot; Orillia &middot; Ontario
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-stone/75">
              A real Canadian waterfront listing in Orillia. Narrated in Canadian
              English. Built from the listing photos in a few minutes. Tap the
              speaker to hear it.
            </p>
          </div>
        </div>
      </div>

      <div aria-hidden className="absolute inset-x-0 bottom-[6px] h-px bg-stone/15" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-[3px] bg-red" />
    </section>
  )
}

function StatStrip() {
  return (
    <section className="bg-cream py-10">
      <div className="mx-auto max-w-3xl px-6">
        <div className="grid grid-cols-2 gap-y-8 sm:grid-cols-4 sm:gap-y-0">
          {STATS.map((stat, i) => (
            <div
              key={stat.label}
              className={`flex flex-col items-center text-center ${
                i > 0 ? "sm:border-l sm:border-stone" : ""
              }`}
            >
              <span className="font-display text-[36px] font-bold leading-none tracking-tight text-charcoal sm:text-[44px]">
                {stat.value}
                <span className="text-red">.</span>
              </span>
              <span className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-warm-grey">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function PainSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28">
      <div className="mx-auto max-w-2xl">
        <p className="text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
          — &nbsp; The problem &nbsp; —
        </p>
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          You&rsquo;re being out-marketed.
          <br />
          Not out-sold
          <span className="text-red">.</span>
        </h2>

        <div className="mt-16 space-y-12">
          {PAIN_POINTS.map((point, i) => {
            const numerals = ["I", "II", "III"]
            return (
              <div key={i} className="flex gap-6">
                <span className="flex-shrink-0 font-display text-[42px] font-bold italic leading-none text-red sm:text-[52px]">
                  {numerals[i]}
                </span>
                <div className="flex-1">
                  <div className="mt-2 h-px w-12 bg-charcoal/20" />
                  <p className="mt-4 text-[16px] leading-[1.7] text-slate sm:text-[17px]">
                    {point}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function StepsSection() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28">
      <div className="mx-auto max-w-2xl">
        <p className="text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
          — &nbsp; How it works &nbsp; —
        </p>
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          Three steps. No learning curve
          <span className="text-red">.</span>
        </h2>
        <ol className="mt-16 space-y-10">
          {STEPS.map((s) => (
            <li key={s.n} className="flex gap-6">
              <span className="flex-shrink-0 font-display text-[42px] font-bold italic leading-none text-red sm:text-[52px]">
                {s.n}
              </span>
              <div className="flex-1">
                <p className="font-display text-[22px] font-bold leading-tight tracking-tight text-charcoal sm:text-[26px]">
                  {s.title}
                  <span className="text-red">.</span>
                </p>
                <p className="mt-3 text-[16px] leading-[1.7] text-slate sm:text-[17px]">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

function BenefitsSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28">
      <div className="mx-auto max-w-3xl">
        <p className="text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
          — &nbsp; What you get &nbsp; —
        </p>
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          A video for every listing.
          <br />
          A look that says &ldquo;serious&rdquo;
          <span className="text-red">.</span>
        </h2>
        <ul className="mt-14 grid gap-6 sm:grid-cols-2">
          {BENEFITS.map((b) => (
            <li key={b.title} className="border-l-2 border-red/60 bg-cream/40 px-6 py-5">
              <p className="font-display text-[19px] font-bold leading-snug tracking-tight text-charcoal">
                {b.title}
              </p>
              <p className="mt-2 text-[14px] leading-[1.65] text-slate">{b.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function DistributionStage() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <p className="text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
            — &nbsp; Listing in. Posts out. &nbsp; —
          </p>
          <h2 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
            Not a video maker.
            <br />
            A publishing pipeline
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-[15px] leading-[1.7] text-slate sm:text-[16px]">
            Most tools stop at &ldquo;here&rsquo;s your video, good luck&rdquo;.
            QuiteRight pulls the photos straight from your Realtor.ca listing,
            renders the cinematic ad, then publishes the right cut to every
            channel that matters. No downloads, no re-uploads, no manual
            scheduling.
          </p>
        </div>

        <div className="mt-16">
          <DistributionDiagram example={CANADA_EXAMPLE} />
        </div>
      </div>
    </section>
  )
}

function MidCta() {
  return (
    <section className="bg-charcoal px-6 py-16 text-center text-white">
      <div className="mx-auto max-w-2xl">
        <h2 className="font-display text-[28px] font-bold leading-tight tracking-tight text-cream sm:text-[36px]">
          Fifty spots. Canadian realtors only
          <span className="text-red">.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-md text-[14px] leading-relaxed text-stone/75">
          Reserve yours before the list closes. We&rsquo;re onboarding by hand —
          first fifty, then the gates open.
        </p>
        <div className="mt-9 flex justify-center">
          <CallToAction tone="dark" />
        </div>
      </div>
    </section>
  )
}

function FaqSection() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28">
      <div className="mx-auto max-w-2xl">
        <p className="text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
          — &nbsp; Questions, answered &nbsp; —
        </p>
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          The bits you&rsquo;re wondering
          <span className="text-red">.</span>
        </h2>
        <dl className="mt-12 divide-y divide-stone">
          {FAQ.map((item, i) => (
            <details key={i} className="group py-5">
              <summary className="flex cursor-pointer items-start justify-between gap-4">
                <dt className="font-display text-[17px] font-semibold leading-snug text-charcoal">
                  {item.q}
                </dt>
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
                  +
                </span>
              </summary>
              <dd className="mt-3 text-[14px] leading-[1.65] text-slate">{item.a}</dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  )
}

function WaitlistFormSection({ initiallySubmitted }: { initiallySubmitted: boolean }) {
  const [showThanks, setShowThanks] = useState(initiallySubmitted)

  const form = useForm({
    lead_type: "canada_waitlist" as const,
    name: "",
    email: "",
    phone: "",
    agency_name: "",
    active_listings: "",
    message: "",
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    track("canada_waitlist_submitted", { campaign: CAMPAIGN_SLUG })
    track("lead_submitted", { campaign: CAMPAIGN_SLUG })
    form.post("/demo", {
      onSuccess: () => {
        setShowThanks(true)
        form.reset()
      },
      preserveScroll: true,
    })
  }

  const inputCx =
    "w-full rounded-md border border-stone bg-white px-4 py-3 text-[15px] text-charcoal placeholder:text-warm-grey/60 transition-colors focus:border-red/40 focus:outline-none focus:ring-2 focus:ring-red/10"
  const labelCx = "block text-[13px] font-semibold uppercase tracking-[0.12em] text-charcoal"
  const errorCx = "mt-1 text-[12px] text-red"

  if (showThanks) {
    return (
      <section id="waitlist-form" className="bg-white px-6 py-20 md:py-28">
        <div className="mx-auto max-w-xl text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
            — &nbsp; You&rsquo;re on the list &nbsp; —
          </p>
          <h2 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[42px]">
            Spot reserved
            <span className="text-red">.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-slate">
            Thanks. We&rsquo;ve got your details. We&rsquo;ll be in touch within a
            few days to walk you through your first listing video and lock in
            founder pricing.
          </p>
          <p className="mt-8 text-[12px] uppercase tracking-[0.22em] text-warm-grey">
            🇨🇦 &nbsp; Welcome aboard
          </p>
        </div>
      </section>
    )
  }

  return (
    <section id="waitlist-form" className="bg-white px-6 py-20 md:py-28">
      <div className="mx-auto max-w-xl">
        <p className="text-center text-[10px] font-semibold uppercase tracking-[0.32em] text-red">
          — &nbsp; Reserve your spot &nbsp; —
        </p>
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[42px]">
          Be one of the first 50
          <span className="text-red">.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-md text-center text-[14px] leading-relaxed text-slate">
          Fill in your details. We&rsquo;ll be in touch within a few days to onboard
          you personally and lock in founder pricing.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 grid gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="ca-name" className={labelCx}>
                Full name <span className="text-red">*</span>
              </label>
              <input
                id="ca-name"
                type="text"
                required
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                placeholder="Jane Tremblay"
                className={`mt-2 ${inputCx}`}
              />
              {form.errors.name && <p className={errorCx}>{form.errors.name}</p>}
            </div>

            <div>
              <label htmlFor="ca-email" className={labelCx}>
                Email <span className="text-red">*</span>
              </label>
              <input
                id="ca-email"
                type="email"
                required
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
                placeholder="jane@brokerage.ca"
                className={`mt-2 ${inputCx}`}
              />
              {form.errors.email && <p className={errorCx}>{form.errors.email}</p>}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="ca-phone" className={labelCx}>
                Phone
              </label>
              <input
                id="ca-phone"
                type="tel"
                value={form.data.phone}
                onChange={(e) => form.setData("phone", e.target.value)}
                placeholder="(416) 555-0143"
                className={`mt-2 ${inputCx}`}
              />
              {form.errors.phone && <p className={errorCx}>{form.errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="ca-agency" className={labelCx}>
                Brokerage <span className="text-red">*</span>
              </label>
              <input
                id="ca-agency"
                type="text"
                required
                value={form.data.agency_name}
                onChange={(e) => form.setData("agency_name", e.target.value)}
                placeholder="RE/MAX, Royal LePage, Sutton…"
                className={`mt-2 ${inputCx}`}
              />
              {form.errors.agency_name && <p className={errorCx}>{form.errors.agency_name}</p>}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="ca-city" className={labelCx}>
                City &amp; province <span className="text-red">*</span>
              </label>
              <input
                id="ca-city"
                type="text"
                required
                value={form.data.message}
                onChange={(e) => form.setData("message", e.target.value)}
                placeholder="Toronto, ON"
                className={`mt-2 ${inputCx}`}
              />
              {form.errors.message && <p className={errorCx}>{form.errors.message}</p>}
            </div>

            <div>
              <label htmlFor="ca-listings" className={labelCx}>
                Listings per month
              </label>
              <select
                id="ca-listings"
                value={form.data.active_listings}
                onChange={(e) => form.setData("active_listings", e.target.value)}
                className={`mt-2 ${inputCx}`}
              >
                <option value="">Select…</option>
                <option value="1-3">1–3</option>
                <option value="4-9">4–9</option>
                <option value="10-19">10–19</option>
                <option value="20+">20+</option>
              </select>
              {form.errors.active_listings && (
                <p className={errorCx}>{form.errors.active_listings}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={form.processing}
            className="group relative mt-2 inline-flex items-center justify-center overflow-hidden rounded-none bg-red px-10 py-5 text-base font-semibold uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_-18px_rgba(196,30,42,0.65)] transition-all duration-300 hover:bg-deep-red disabled:opacity-50"
          >
            <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/15 to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-90" />
            <span className="relative">{form.processing ? "Reserving…" : "Reserve my spot"}</span>
          </button>

          <p className="text-center text-[11px] uppercase tracking-[0.18em] text-warm-grey">
            Free. No card. We&rsquo;ll be in touch within a few days.
          </p>
        </form>
      </div>
    </section>
  )
}

function LegalFooter() {
  return (
    <footer className="bg-cream px-6 py-8 text-center">
      <p className="text-[11px] uppercase tracking-[0.18em] text-warm-grey">
        © QuiteRight &nbsp;·&nbsp;{" "}
        <a href="/privacy" className="underline hover:text-charcoal">Privacy</a>
        {" "}·{" "}
        <a href="/terms" className="underline hover:text-charcoal">Terms</a>
      </p>
    </footer>
  )
}

export default function CanadaFirst50Lp({ meta, submitted = false }: Props) {
  const fired15s = useRef(false)

  useEffect(() => {
    track("lp_view", { campaign: CAMPAIGN_SLUG })
    const t = window.setTimeout(() => {
      if (!fired15s.current) {
        track("lp_view_15s", { campaign: CAMPAIGN_SLUG })
        fired15s.current = true
      }
    }, 15_000)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
      </Head>

      <main className="min-h-screen bg-cream">
        <HeroStage />
        <StatStrip />
        <PainSection />
        <StepsSection />
        <DistributionStage />
        <BenefitsSection />
        <MidCta />
        <FaqSection />
        <WaitlistFormSection initiallySubmitted={submitted} />
        <LegalFooter />
      </main>
    </>
  )
}
